import { useCallback, useEffect, useRef } from 'react';

import { FormControl, FormLabel, Switch, type SwitchProps } from '@vimeo/bokeh';
import useLocalStorage from '@vimeo/core/hooks/use-storage/useLocalStorage';
import { translate } from '@vimeo/i18n-runtime';

interface Autoplay {
  value: boolean;
  expiry: number;
}
export interface AutoplayToggleProps extends SwitchProps {
  /** the unique id for toggle and its label, also served as the key for autoplay session storage item */
  id: string;
  /** expiry for toggle preference in local storage, in days, default to 1 day */
  ttl?: number;
  /** callback whenever toggle checked state changes, this handler will fire when local storage value set when component mounted */
  onCheckedChange?: (checked: boolean) => void;
}

const dayEpoch = 86123353;

export const AutoplayToggle = ({
  id,
  ttl = 1,
  defaultChecked = false,
  onChange,
  onCheckedChange,
  ...rest
}: AutoplayToggleProps) => {
  const defaultAutoplayMode = useRef<Autoplay>({
    value: defaultChecked,
    expiry: new Date().getTime() + ttl * dayEpoch,
  });

  const [autoplayMode, setAutoplayMode] = useLocalStorage<Autoplay>(
    id,
    defaultAutoplayMode.current,
  );

  /**
   * `sessionPlayMode` can return
   * - value not in storage, `autoplayMode` will be the same `defaultAutoplayMode`, not pass expiry check. Only trigger `onCheckedChange` to lift the current autoplay value up
   * - value in storage, return the autoplayMode in local storage, check expiry
   *  - if not expired, not pass expiry check, only trigger `onCheckedChange` to lift the current autoplay value up
   *  - if expired, set local storage with current value `defaultAutoplayMode` to refresh the expiry
   */
  useEffect(() => {
    if (autoplayMode.expiry < new Date().getTime()) {
      setAutoplayMode(defaultAutoplayMode.current);
      onCheckedChange?.(defaultAutoplayMode.current.value);

      return;
    }

    onCheckedChange?.(autoplayMode.value);
  }, [autoplayMode, onCheckedChange, setAutoplayMode]);

  const onToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setAutoplayMode({
        value: event.target.checked,
        expiry: new Date().getTime() + ttl * dayEpoch,
      });

      onChange?.(event); // unlike `onCheckedChange`, this is to handle the native onChange event, this won't fire when initial value from localstorage is set
    },
    [ttl, onChange, setAutoplayMode],
  );

  return (
    <FormControl display="flex" alignItems="center" gap="75">
      <FormLabel htmlFor={id} color="text-primary" fontSize="body-md" my="0">
        {translate('Autoplay next')}
      </FormLabel>
      <Switch
        id={id}
        size="sm"
        defaultChecked={autoplayMode.value}
        onChange={onToggleChange}
        {...rest}
      />
    </FormControl>
  );
};
